import React, { useEffect, useRef, useState } from "react";
import "./EditarModal.css";

const UP = (v) => (typeof v === "string" ? v.toLocaleUpperCase("pt-BR") : v ?? "");

function onlyDigits(s) {
  return String(s || "").replace(/\D/g, "");
}

function formatPhoneBR(v) {
  const d = onlyDigits(v).slice(0, 11);
  const ddd = d.slice(0, 2);
  const p1 = d.slice(2, 3);
  const p2 = d.slice(3, 7);
  const p3 = d.slice(7, 11);

  if (!d.length) return "";
  if (d.length <= 2) return `(${ddd}`;
  if (d.length <= 3) return `(${ddd}) ${p1}`;
  if (d.length <= 7) return `(${ddd}) ${p1} ${p2}`;
  return `(${ddd}) ${p1} ${p2}-${p3}`;
}

function isValidPhoneBR(v) {
  return onlyDigits(v).length === 11;
}

export default function EditarModal({ open, onClose, onSalvar, registro }) {
  const [form, setForm] = useState({
    ativos: "",
    atividade: "",
    responsavel: "",
    telefone_do_responsavel: "",
    grupo: "",
    operacao: "METRICA",
    empresa: "",
    coordenador: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const ativosRef = useRef(null);
  const atividadeRef = useRef(null);
  const responsavelRef = useRef(null);
  const telefoneRef = useRef(null);
  const grupoRef = useRef(null);

  useEffect(() => {
    if (!open || !registro) return;

    setErr("");
    setBusy(false);

    setForm({
      ativos: UP(registro.ativos ?? ""),
      atividade: UP(registro.atividade ?? ""),
      responsavel: UP(registro.responsavel ?? ""),
      telefone_do_responsavel: formatPhoneBR(registro.telefone_do_responsavel ?? ""),
      grupo: UP(registro.grupo ?? ""),
      operacao: "METRICA",
      empresa: UP(registro.empresa ?? ""),
      coordenador: UP(registro.coordenador ?? ""),
    });

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => ativosRef.current?.focus(), 0);

    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [open, registro]);

  if (!open || !registro) return null;

  const handleChangeUpper = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: UP(value) }));
  };

  const handlePhoneChange = (e) => {
    const masked = formatPhoneBR(e.target.value);
    setForm((s) => ({ ...s, telefone_do_responsavel: masked }));
  };

  async function handleSalvar() {
    setBusy(true);
    setErr("");

    try {
      if (!form.ativos.trim()) {
        ativosRef.current?.focus();
        throw new Error("Informe o nome do ativo.");
      }
      if (!form.atividade.trim()) {
        atividadeRef.current?.focus();
        throw new Error("Informe a atividade.");
      }
      if (!form.responsavel.trim()) {
        responsavelRef.current?.focus();
        throw new Error("Informe o responsavel.");
      }
      if (!form.telefone_do_responsavel.trim()) {
        telefoneRef.current?.focus();
        throw new Error("Informe o telefone do responsavel.");
      }
      if (!isValidPhoneBR(form.telefone_do_responsavel)) {
        telefoneRef.current?.focus();
        throw new Error("Telefone invalido. Use o padrao (99) 9 9999-9999.");
      }
      if (!form.grupo.trim()) {
        grupoRef.current?.focus();
        throw new Error("Informe o grupo.");
      }

      const payload = {
        ativos: UP(form.ativos),
        atividade: UP(form.atividade),
        responsavel: UP(form.responsavel),
        telefone_do_responsavel: form.telefone_do_responsavel,
        grupo: UP(form.grupo),
        operacao: "METRICA",
        empresa: UP(form.empresa),
        coordenador: UP(form.coordenador),
      };

      await onSalvar?.(payload);
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Falha ao salvar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ed-modal__overlay" data-edmd onClick={onClose}>
      <div className="ed-modal__card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Editar ativo">
        <div className="ed-modal__head">
          <div className="ed-modal__title">Editar Ativo</div>
          <button className="ed-modal__x" onClick={onClose} disabled={busy} type="button" aria-label="Fechar" title="Fechar">
            x
          </button>
        </div>

        <div className="ed-modal__body grid2">
          <label className="ed-row">
            <span className="ed-label">Nome do ativo *</span>
            <input ref={ativosRef} name="ativos" className="ed-input" value={form.ativos} onChange={handleChangeUpper} autoComplete="off" />
          </label>

          <label className="ed-row">
            <span className="ed-label">Atividade *</span>
            <input ref={atividadeRef} name="atividade" className="ed-input" value={form.atividade} onChange={handleChangeUpper} autoComplete="off" />
          </label>

          <label className="ed-row">
            <span className="ed-label">Responsavel *</span>
            <input ref={responsavelRef} name="responsavel" className="ed-input" value={form.responsavel} onChange={handleChangeUpper} autoComplete="off" />
          </label>

          <label className="ed-row">
            <span className="ed-label">Telefone do responsavel *</span>
            <input
              ref={telefoneRef}
              name="telefone_do_responsavel"
              className="ed-input"
              value={form.telefone_do_responsavel}
              onChange={handlePhoneChange}
              autoComplete="off"
              inputMode="numeric"
              placeholder="(99) 9 9999-9999"
            />
          </label>

          <label className="ed-row">
            <span className="ed-label">Grupo *</span>
            <input ref={grupoRef} name="grupo" className="ed-input" value={form.grupo} onChange={handleChangeUpper} autoComplete="off" />
          </label>

          <label className="ed-row">
            <span className="ed-label">Operacao *</span>
            <input name="operacao" className="ed-input" value="METRICA" readOnly title="Operacao fixa" />
          </label>

          <label className="ed-row">
            <span className="ed-label">Empresa</span>
            <input name="empresa" className="ed-input" value={form.empresa} onChange={handleChangeUpper} autoComplete="off" />
          </label>

          <label className="ed-row">
            <span className="ed-label">Coordenador</span>
            <input name="coordenador" className="ed-input" value={form.coordenador} onChange={handleChangeUpper} autoComplete="off" />
          </label>

          {err && <div className="ed-err ed-span2">{err}</div>}
        </div>

        <div className="ed-modal__foot">
          <button className="ed-btn ghost" onClick={onClose} disabled={busy} type="button">Cancelar</button>
          <button className="ed-btn primary" onClick={handleSalvar} disabled={busy} type="button">
            {busy ? "Salvando..." : "Salvar alteracoes"}
          </button>
        </div>
      </div>
    </div>
  );
}
